import {DocumentData, OrderByDirection, QueryOption} from '@coolcolduk/firebase-util';
import {castArray, filterArrayUndefined} from '@coolcolduk/util';
import {
  CollectionReference,
  Query,
  QueryConstraint,
  QuerySnapshot,
  endAt,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
} from 'firebase/firestore';

function buildLimitConstraint(limitN?: number, limitToLastN?: number): QueryConstraint | undefined {
  if (limitToLastN !== undefined) {
    return limitToLast(limitToLastN);
  }
  if (limitN !== undefined) {
    return limit(limitN);
  }
  return undefined;
}

function cursorConstraint(
  kind: 'startAt' | 'startAfter' | 'endAt' | 'endBefore',
  value: QueryOption['startAt'],
): QueryConstraint | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    if (kind === 'startAt') {
      return startAt(...value);
    }
    if (kind === 'startAfter') {
      return startAfter(...value);
    }
    if (kind === 'endAt') {
      return endAt(...value);
    }
    return endBefore(...value);
  }
  if (kind === 'startAt') {
    return startAt(value as never);
  }
  if (kind === 'startAfter') {
    return startAfter(value as never);
  }
  if (kind === 'endAt') {
    return endAt(value as never);
  }
  return endBefore(value as never);
}

function buildQueryConstraints(options: QueryOption): QueryConstraint[] {
  const whereConstraints = castArray(options.where || []).map((f) => where(f.field, f.operator, f.value));
  const orderConstraints = castArray(options.order || []).map((o) =>
    orderBy(o.field, o.direction ?? OrderByDirection.ASC),
  );
  const cursorConstraints = [
    cursorConstraint('startAt', options.startAt),
    cursorConstraint('startAfter', options.startAfter),
    cursorConstraint('endAt', options.endAt),
    cursorConstraint('endBefore', options.endBefore),
  ];
  const limitC = buildLimitConstraint(options.limit, options.limitToLast);
  return filterArrayUndefined([...whereConstraints, ...orderConstraints, ...cursorConstraints, limitC]);
}

/**
 * Builds a Firestore query from {@link QueryOption}, runs it, maps documents, and drops entries the mapper omits.
 * @param ref Collection or existing query to constrain
 * @param options Where / order / cursor / limit options
 * @param mapper Per-document map; return `undefined` to exclude a document
 */
export default async function buildFirestoreQuery<
  AppModelType extends DocumentData,
  DbModelType extends DocumentData = DocumentData,
>(
  ref: CollectionReference<AppModelType, DbModelType> | Query<AppModelType, DbModelType>,
  options: QueryOption,
): Promise<QuerySnapshot<AppModelType, DbModelType>> {
  const q = query(ref, ...buildQueryConstraints(options));
  return getDocs(q);
}
